import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {EMPTY, of} from 'rxjs';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {StockHistoryService} from '../services/stock-history.service';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {setSelectedNotification} from '../../notifications/store/notification.actions';
import * as StockHistoryActions from './stock-history.actions';
import {selectStockHistoryCurrentStoreId, selectStockHistoryState} from './stock-history.selectors';
import {selectColleagues} from '../../core/store-shared/shared.selectors';
import {INotification} from '../../notifications/models';
import {Timestamp} from '@angular/fire/firestore';
import {readOperationalEventTimestamp} from '../../../shared/shared-utils/firestore/operational-server-timestamp.utils';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {IStockHistoryEvent, IStockHistoryPageResult} from '../models/stock-history-interfaces';

@Injectable()
export class StockHistoryEffects {

  loadFirstPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.loadStockHistoryFirstPage),
      withLatestFrom(this.store.pipe(select(selectColleagues))),
      switchMap(([{storeId, itemCodes, field}, colleagues]) => {
        const codes: string[] = itemCodes ?? [];
        if (!storeId || codes.length === 0) {
          return of(StockHistoryActions.loadStockHistorySuccess({
            events: [],
            lastDocumentId: null,
            hasMore: true,
            isFirstPage: true,
          }));
        }
        return this.stockHistoryService.getStockHistoryPage(storeId, codes, field || 'All', colleagues).pipe(
          map((result: IStockHistoryPageResult) => StockHistoryActions.loadStockHistorySuccess({
            events: result.events,
            lastDocumentId: result.lastDocumentId,
            hasMore: result.hasMore,
            isFirstPage: true,
          })),
          catchError(error => {
            const globalError: IGlobalError = {
              action: '[Stock History] Load First Page',
              actionType: 'StockHistory.LoadFirstPage',
              message: 'Failed to load stock history',
              name: ErrorName.ActionFailedError,
              originalError: error,
            };
            return of(
              StockHistoryActions.loadStockHistoryFailure(),
              NotificationActions.setGlobalErrorOccurrence({error: globalError}),
            );
          }),
        );
      }),
    ),
  );

  loadNextPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.loadStockHistoryNextPage),
      withLatestFrom(
        this.store.pipe(select(selectStockHistoryState)),
        this.store.pipe(select(selectColleagues)),
      ),
      switchMap(([_, historyState, colleagues]) => {
        const queryEpochAtRequest: number = historyState.queryEpoch;
        const {currentStoreId, events, itemCodes, selectedField, lastDocumentId} = historyState;
        const storeId: string = currentStoreId ?? '';
        if (!storeId || itemCodes.length === 0) {
          return EMPTY;
        }
        return this.stockHistoryService.getStockHistoryPage(
          storeId,
          itemCodes,
          selectedField,
          colleagues,
          lastDocumentId,
        ).pipe(
          withLatestFrom(this.store.pipe(select(selectStockHistoryState))),
          switchMap(([result, currentState]) => {
            if (currentState.queryEpoch !== queryEpochAtRequest) {
              return EMPTY;
            }
            const uniqueFiltered: IStockHistoryEvent[] = result.events.filter((evt: IStockHistoryEvent) =>
              !events.some((prev: IStockHistoryEvent) => prev.id === evt.id),
            );
            return of(StockHistoryActions.loadStockHistorySuccess({
              events: uniqueFiltered,
              lastDocumentId: result.lastDocumentId,
              hasMore: result.hasMore,
              isFirstPage: false,
            }));
          }),
          catchError(error => {
            const globalError: IGlobalError = {
              action: '[Stock History] Load Next Page',
              actionType: 'StockHistory.LoadNextPage',
              message: 'Failed to load more stock history items',
              name: ErrorName.ActionFailedError,
              originalError: error,
            };
            return of(
              StockHistoryActions.loadStockHistoryFailure(),
              NotificationActions.setGlobalErrorOccurrence({error: globalError}),
            );
          }),
        );
      }),
    ),
  );

  searchTermChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.updateStockHistorySearchTerm),
      withLatestFrom(
        this.store.select(selectSelectedStore),
        this.store.select(selectStockHistoryCurrentStoreId),
      ),
      switchMap(([{itemCodes, field}, selectedStore, currentStoreId]) => {
        const storeId: string = selectedStore?.storeId ?? currentStoreId ?? '';
        if (!storeId || !itemCodes?.length) {
          return EMPTY;
        }
        return of(StockHistoryActions.loadStockHistoryFirstPage({storeId, itemCodes, field}));
      }),
    ),
  );

  openDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.openStockHistoryDetailView),
      map(({event}) => {
        const eventDate = readOperationalEventTimestamp(event.creationDate) ?? new Date(0);
        const notification: Partial<INotification> = {
          notificationId: (event).id || '',
          response: '',
          storeId: event.userId,
          success: event.status === 'COMPLETE',
          timestamp: Timestamp.fromDate(eventDate),
          type: event.type,
          event: 'STOCK_UPDATE',
        };
        return setSelectedNotification({notification: notification as INotification});
      }),
    ),
  );

  loadItemOrigins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.updateStockHistorySearchTerm),
      withLatestFrom(
        this.store.select(selectSelectedStore),
        this.store.select(selectStockHistoryCurrentStoreId),
        this.store.pipe(select(selectColleagues)),
      ),
      switchMap(([{itemCodes, selectedItems}, selectedStore, currentStoreId, colleagues]) => {
        const storeId: string = selectedStore?.storeId ?? currentStoreId ?? '';
        const snapshots = selectedItems?.length
          ? selectedItems
          : (itemCodes ?? []).map((code: string) => ({code}));
        if (!storeId || snapshots.length === 0) {
          return of(StockHistoryActions.loadStockHistoryItemOriginsSuccess({itemOrigins: []}));
        }
        return this.stockHistoryService.getItemCreationMeta(storeId, snapshots, colleagues).pipe(
          map((itemOrigins) => StockHistoryActions.loadStockHistoryItemOriginsSuccess({itemOrigins})),
          catchError(() => of(StockHistoryActions.loadStockHistoryItemOriginsSuccess({itemOrigins: []}))),
        );
      }),
    ),
  );

  openModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockHistoryActions.openStockHistoryModal),
      withLatestFrom(this.store.select(selectSelectedStore)),
      map(([, store]) => StockHistoryActions.stockHistorySetStoreContext({storeId: store?.storeId ?? ''})),
    ),
  );

  constructor(
    private actions$: Actions,
    private stockHistoryService: StockHistoryService,
    private readonly store: Store,
  ) {
  }
}
